import axios from "axios";
import DOMPurify from 'dompurify';

const dev = process.env.NODE_ENV === "dev";
const apiURL: string = import.meta.env.VITE_DEV_URL;

export class Api {
	public static GetSHESecret = async (uuid: string, password: string) => {
		uuid = DOMPurify.sanitize(uuid);
		password = DOMPurify.sanitize(password);

		type ReturnType = { data: { cyphertext: string; second_half_key: string; iv: string; fragment_private_key?: string } };
		return new Promise(async (resolve: (value: ReturnType) => void, reject) => {
			await axios
				.post(`/api/getSHE/${uuid}`, { password: encodeURIComponent(password) }, {
					headers: {
						"Content-Type": "application/json",
					},
				})
				.then((res: ReturnType) => {
					resolve(res);
				})
				.catch((error) => {
					reject(error.response.data);
				});
		});
	};

	public static PostSHESecret = async (valuesObject: { cyphertext: string; second_half_key: string; password?: string; password_salt?: string }) => {
		valuesObject = {
			cyphertext: DOMPurify.sanitize(valuesObject.cyphertext),
			second_half_key: DOMPurify.sanitize(valuesObject.second_half_key),
			password: encodeURIComponent(DOMPurify.sanitize(valuesObject.password || "")),
			password_salt: valuesObject.password_salt && DOMPurify.sanitize(valuesObject.password_salt),
		}

		return new Promise(async (resolve: (value: { id: string; public_key: string }) => void, reject) => {
			await axios
				.post(`/api/addSHE`, valuesObject, {
					headers: {
						"Content-Type": "application/json",
					},
				})
				.then((res) => {
					resolve({ id: res.data.id, public_key: res.data.public_key });
				})
				.catch((error) => {
					reject(error.response.data);
				});
		});
	};

	public static GetE2ESecret = async (uuid: string) => {
		uuid = DOMPurify.sanitize(uuid);

		return new Promise(async (resolve: (value: string) => void, reject) => {
			await axios
				.post(`/api/getE2E/${uuid}`, {
					headers: {
						"Content-Type": "application/json",
					},
				})
				.then((res) => {
					resolve(res.data.cyphertext);
				})
				.catch((error) => {
					reject("Error getting secret: " + error.message);
				});
		});
	};

	public static PostE2ESecret = async (encryptedSecret: string, loadedPublicKey: string) => {
		encryptedSecret = DOMPurify.sanitize(encryptedSecret);
		loadedPublicKey = DOMPurify.sanitize(loadedPublicKey);

		return new Promise(async (resolve: (value: string) => void, reject) => {
			await axios
				.post(
					`/api/addE2E`,
					{
						cyphertext: encryptedSecret,
						public_key_uuid: loadedPublicKey,
					},
					{
						headers: {
							"Content-Type": "application/json",
						},
					}
				)
				.then((res) => {
					resolve(res.data.id);
				})
				.catch((error) => {
					reject(`Error posting secret: ${error.message}`);
				});
		});
	};

	public static GetStatus = async (uuid: string) => {
		uuid = DOMPurify.sanitize(uuid);

		return new Promise(async (resolve: (value: { is_available: boolean; passwordProtected: boolean; version: number; password_salt?: string }) => void, reject) => {
			await axios
				.get(`/api/status/${uuid}`, {
					headers: {
						"Content-Type": "application/json",
					},
				})
				.then((res) => {
					resolve(res.data);
				})
				.catch((error) => {
					reject(`Error getting status: ${error.message}`);
				});
		});
	};

	public static GetPublicKey = async (uuid: string) => {
		uuid = DOMPurify.sanitize(uuid);

		return new Promise(async (resolve: (value: string) => void, reject) => {
			await axios
				.get(`/api/getpublickey/${uuid}`, {
					headers: {
						"Content-Type": "application/json",
					},
				})
				.then((res) => {
					resolve(
						res.data.public_key
							.replace(/\\n/g, "\n")
							.replace(/\r?\n(?!\r?\n)/g, "\n")
							.replace(/\n$/, "")
					);
				})
				.catch((error) => {
					reject("Error getting secret: " + error.message);
				});
		});
	};

	public static PostPublicKey = async (publicKey: string) => {
		publicKey = DOMPurify.sanitize(publicKey);

		return new Promise(async (resolve: (value: string) => void, reject) => {
			await axios
				.post(
					`/api/postpublickey`,
					{
						public_key: publicKey.replace(/\r?\n+$/, "").replace(/\r?\n(?!\r?\n)/g, "\\n"),
					},
					{
						headers: {
							"Content-Type": "application/json",
						},
					}
				)
				.then((res) => {
					resolve(res.data.id);
				})
				.catch((error) => {
					reject(`Error posting secret: ${error.message}`);
				});
		});
	};
}
